import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { ChartPie, Users, CaretDown, CaretUp, FileCsv, FilePdf, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getTriageBadgeClasses = (color) => {
  const map = { Green: 'bg-green-500 text-white', Yellow: 'bg-yellow-400 text-black', Orange: 'bg-orange-500 text-white', Red: 'bg-red-600 text-white', Black: 'bg-black text-white' };
  return map[color] || 'bg-slate-200 text-slate-700';
};

const getDispositionLabel = (patient) => {
  if (patient.is_transported) return { label: 'Transport', className: 'bg-blue-100 text-blue-700' };
  if (patient.rma_signature) return { label: 'RMA', className: 'bg-orange-100 text-orange-700' };
  return { label: 'In Triage', className: 'bg-slate-100 text-slate-700' };
};

export function EventDashboardTab({ token }) {
  const [dashboardData, setDashboardData] = useState([]);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [eventPatients, setEventPatients] = useState({});
  const [loadingPatients, setLoadingPatients] = useState({});

  const fetchDashboard = useCallback(async () => {
    setLoadingDashboard(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/reports/event-dashboard`, {});
      setDashboardData(data);
    } catch { toast.error('Failed to load event dashboard'); }
    finally { setLoadingDashboard(false); }
  }, [token]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleExportEventCSV = async (eventName) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/reports/event-export-csv?event_name=${encodeURIComponent(eventName)}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `event_report_${eventName.replace(/\s+/g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`CSV exported for ${eventName}`);
    } catch { toast.error('Failed to export CSV'); }
  };

  const toggleEventPatients = async (eventName) => {
    const isExpanded = expandedEvents[eventName];
    setExpandedEvents(prev => ({ ...prev, [eventName]: !isExpanded }));
    if (!isExpanded && !eventPatients[eventName]) {
      setLoadingPatients(prev => ({ ...prev, [eventName]: true }));
      try {
        const { data } = await axios.get(
          `${API_URL}/api/reports/event-patients?event_name=${encodeURIComponent(eventName)}`,
          {}
        );
        setEventPatients(prev => ({ ...prev, [eventName]: data }));
      } catch { toast.error('Failed to load patients'); }
      finally { setLoadingPatients(prev => ({ ...prev, [eventName]: false })); }
    }
  };

  if (loadingDashboard) {
    return (
      <div className="text-center py-12">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-500">Loading event statistics...</p>
      </div>
    );
  }

  if (dashboardData.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-12 text-center">
          <ChartPie size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">No event data available yet.</p>
          <p className="text-slate-400 text-sm mt-1">Create events and add patients to see statistics here.</p>
        </CardContent>
      </Card>
    );
  }

  const totalPatients = dashboardData.reduce((sum, ev) => sum + ev.total_patients, 0);
  const eventsWithPatients = dashboardData.filter(e => e.total_patients > 0);
  const avgTransportRate = eventsWithPatients.length > 0
    ? (dashboardData.reduce((sum, ev) => sum + ev.transport_rate, 0) / eventsWithPatients.length).toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Summary Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Events</p>
            <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="total-events-count">{dashboardData.length}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Patients (All Events)</p>
            <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="total-patients-count">{totalPatients}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Avg Transport Rate</p>
            <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="avg-transport-rate">{avgTransportRate}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Per-Event Cards */}
      {dashboardData.map((ev) => (
        <Card key={ev.event_name} className="border-slate-200" data-testid={`event-card-${ev.event_name.replace(/\s+/g, '-').toLowerCase()}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">{ev.event_name}</CardTitle>
                <CardDescription>
                  {ev.earliest_date && ev.latest_date
                    ? `${new Date(ev.earliest_date).toLocaleDateString()} — ${new Date(ev.latest_date).toLocaleDateString()}`
                    : 'No patient records yet'}
                </CardDescription>
              </div>
              <div className="flex items-center gap-3">
                {ev.total_patients > 0 && (
                  <div className="flex gap-2 no-print">
                    <Button variant="outline" size="sm" className="rounded-sm" onClick={() => handleExportEventCSV(ev.event_name)} data-testid={`export-csv-${ev.event_name.replace(/\s+/g, '-').toLowerCase()}`}>
                      <FileCsv size={16} className="mr-1" /> CSV
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-sm" onClick={() => window.print()} data-testid={`export-pdf-${ev.event_name.replace(/\s+/g, '-').toLowerCase()}`}>
                      <FilePdf size={16} className="mr-1" /> PDF
                    </Button>
                  </div>
                )}
                <span className="text-2xl font-bold text-slate-900">{ev.total_patients} <span className="text-sm font-normal text-slate-500">patients</span></span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {ev.total_patients === 0 ? (
              <p className="text-slate-400 text-sm py-4 text-center">No patients recorded for this event yet.</p>
            ) : (
              <div className="space-y-4">
                {/* Triage Breakdown */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Triage Breakdown</p>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { color: 'Green', bg: 'bg-green-500', text: 'text-white' },
                      { color: 'Yellow', bg: 'bg-yellow-400', text: 'text-black' },
                      { color: 'Orange', bg: 'bg-orange-500', text: 'text-white' },
                      { color: 'Red', bg: 'bg-red-600', text: 'text-white' },
                      { color: 'Black', bg: 'bg-black', text: 'text-white' },
                    ].map(({ color, bg, text }) => (
                      <div key={color} className={`${bg} ${text} px-3 py-2 rounded-sm text-sm font-medium min-w-[80px] text-center`}>
                        {color}: {ev.triage[color]}
                      </div>
                    ))}
                  </div>
                  <div className="flex h-3 rounded-full overflow-hidden mt-2">
                    {ev.triage.Green > 0 && <div className="bg-green-500" style={{ width: `${(ev.triage.Green / ev.total_patients) * 100}%` }} />}
                    {ev.triage.Yellow > 0 && <div className="bg-yellow-400" style={{ width: `${(ev.triage.Yellow / ev.total_patients) * 100}%` }} />}
                    {ev.triage.Orange > 0 && <div className="bg-orange-500" style={{ width: `${(ev.triage.Orange / ev.total_patients) * 100}%` }} />}
                    {ev.triage.Red > 0 && <div className="bg-red-600" style={{ width: `${(ev.triage.Red / ev.total_patients) * 100}%` }} />}
                    {ev.triage.Black > 0 && <div className="bg-black" style={{ width: `${(ev.triage.Black / ev.total_patients) * 100}%` }} />}
                  </div>
                </div>

                {/* Disposition Stats */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Patient Disposition</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-center">
                      <p className="text-2xl font-bold text-blue-700">{ev.transported}</p>
                      <p className="text-xs text-blue-600 font-medium">Transported</p>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-sm p-3 text-center">
                      <p className="text-2xl font-bold text-orange-700">{ev.rma}</p>
                      <p className="text-xs text-orange-600 font-medium">RMA</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-center">
                      <p className="text-2xl font-bold text-slate-700">{ev.on_scene}</p>
                      <p className="text-xs text-slate-600 font-medium">In Triage</p>
                    </div>
                  </div>
                </div>

                {/* Transport Rate */}
                <div className="flex items-center gap-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Transport Rate:</p>
                  <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                    <div className="bg-blue-500 h-full rounded-full transition-all duration-500" style={{ width: `${ev.transport_rate}%` }} />
                  </div>
                  <span className="text-sm font-bold text-slate-700">{ev.transport_rate}%</span>
                </div>

                {/* Expand/Collapse Patient List */}
                <div className="border-t border-slate-200 pt-3">
                  <Button variant="ghost" className="w-full justify-between text-slate-700 hover:bg-slate-50" onClick={() => toggleEventPatients(ev.event_name)} data-testid={`toggle-patients-${ev.event_name.replace(/\s+/g, '-').toLowerCase()}`}>
                    <span className="flex items-center gap-2 text-sm font-medium"><Users size={16} /> View All Patients ({ev.total_patients})</span>
                    {expandedEvents[ev.event_name] ? <CaretUp size={16} /> : <CaretDown size={16} />}
                  </Button>

                  {expandedEvents[ev.event_name] && (
                    <div className="mt-3" data-testid={`patient-list-${ev.event_name.replace(/\s+/g, '-').toLowerCase()}`}>
                      {loadingPatients[ev.event_name] ? (
                        <div className="text-center py-6">
                          <div className="w-6 h-6 border-3 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                          <p className="text-sm text-slate-500">Loading patients...</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto rounded-sm border border-slate-200">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Patient</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">DOB</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Triage</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Reason</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Contact Time</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Provider</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {(eventPatients[ev.event_name] || []).map((pt) => {
                                const disposition = getDispositionLabel(pt);
                                return (
                                  <tr key={pt.id} className="hover:bg-slate-50" data-testid={`patient-row-${pt.id}`}>
                                    <td className="px-3 py-2 font-medium text-slate-900">{pt.first_name} {pt.last_name}</td>
                                    <td className="px-3 py-2 text-slate-600">{pt.dob || '—'}</td>
                                    <td className="px-3 py-2">
                                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${getTriageBadgeClasses(pt.tag_color)}`}>{pt.tag_color || '—'}</span>
                                    </td>
                                    <td className="px-3 py-2 text-slate-600">{pt.reason_for_treatment || '—'}</td>
                                    <td className="px-3 py-2">
                                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${disposition.className}`}>{disposition.label}</span>
                                      {pt.is_transported && pt.transported_by && <span className="block text-xs text-slate-400 mt-0.5">by {pt.transported_by}</span>}
                                    </td>
                                    <td className="px-3 py-2 text-slate-600">{pt.patient_contact_time || '—'}</td>
                                    <td className="px-3 py-2 text-slate-600 text-xs">{pt.created_by_name || '—'}</td>
                                    <td className="px-3 py-2">
                                      <Link to={`/patient/${pt.id}`}>
                                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`view-patient-${pt.id}`}><Eye size={14} /></Button>
                                      </Link>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
